import { atomWithKey as atom } from "./atomWithKey";
import {
  IAddonsPassengerLeg,
  IAlreadyAddonsForPassenger,
  IAlreadyAddonsLegPassengerAddons,
  IBookingLegWiseAddonsForCancelTrip,
  ICarriedOverAddons,
  ITransformedMealForLegs,
} from "../Types/Interface/IAddOns";

export const mealPreferencesResponseAtom = atom<ITransformedMealForLegs>({
  key: "mealPreferencesResponseAtom",
  default: {},
});

export const showAddonsModalAtom = atom<boolean>({
  key: "showAddonsModalAtom",
  default: false,
});
export const addonsPassengerListAtom = atom<IAddonsPassengerLeg[]>({
  key: "addonsPassengerListAtom",
  default: [],
});
export const legWiseAddonsForPassengerAtom = atom<IAddonsPassengerLeg[]>({
  key: "legWiseAddonsForPassengerAtom",
  default: [],
});
export const selectedLegAddonsPriceAtom = atom<number>({
  key: "selectedLegAddonsPriceAtom",
  default: 0,
});
export const confirmAddonsPassengerListAtom = atom<IAddonsPassengerLeg[]>({
  key: "confirmAddonsPassengerListAtom",
  default: [],
});
export const alreadyAddonsLegPassengerAtom = atom<IAlreadyAddonsLegPassengerAddons>({
  key: "alreadyAddonsLegPassengerAtom",
  default: {},
});

export const manageSeatingBaselineAddonsLegPassengerAtom =
  atom<IAlreadyAddonsLegPassengerAddons>({
    key: "manageSeatingBaselineAddonsLegPassengerAtom",
    default: {},
  });
export const alreadyAddonsLegPassengerTotalAtom = atom<Record<string, { legId: string; totalPrice: number }>>({
  key: "alreadyAddonsLegPassengerTotalAtom",
  default: {},
});
export const manageAddonsNewConfirmedAddonsAtom = atom<IAddonsPassengerLeg[]>({
  key: "manageAddonsNewConfirmedAddonsAtom",
  default: [],
});
export const manageAddonsRemovedAddonsAtom = atom<IAlreadyAddonsForPassenger[]>({
  key: "manageAddonsRemovedAddonsAtom",
  default: [],
});

export const patchAddonsOnConfirmAtom = atom<boolean>({
  key: "patchAddonsOnConfirmAtom",
  default: true,
});
export const bookingLegWiseAddonsForCancelTripAtom = atom<IBookingLegWiseAddonsForCancelTrip>({
  key: "bookingLegWiseAddonsForCancelTripAtom",
  default: {
    inbound: {
      addons: [],
      total: 0
    },
    outbound: {
      addons: [],
      total: 0
    }
  }
});
export const addonFlowStartedAtom = atom<Boolean>({
  key: "addonFlowStartedAtom",
  default: false,
});
/**
 * Fulfillment IDs of FULFILLED ancillaries that the user deselected during
 * manage-seating, scoped to the booking they belong to. PersistAddonsOnConfirmAction
 * skips DELETE on FULFILLED items (you can't DELETE a paid ancillary; refunds
 * go through a different pipeline) and parks the IDs here.
 *
 * Refund settlement is server-side: buildSessionAncillaries reads this queue
 * on Pay-click and sends the IDs in the `refundedAncillaries` array of the
 * /api/payment/session POST body. The backend processes the refund as part
 * of the gateway session, so a cancelled session means no refund — and a
 * settled session bundles the refund with the new charge atomically.
 *
 * Per-leg recompute: PersistAddonsOnConfirmAction recomputes this queue at
 * every modal-Save with per-leg scoping, so a re-ticked previously-un-ticked
 * FULFILLED item gets removed from the queue. Out-of-scope legs (multi-leg
 * flows where another leg has been confirmed separately) keep their entries.
 *
 * The bookingId scope is load-bearing: the atom is whitelisted and persists
 * across SPA navigations / page refreshes, so a user who abandons manage-seating
 * mid-flow could otherwise have stale IDs drift against an unrelated booking.
 * buildSessionAncillaries guards by `pending.bookingId === bookingId`.
 *
 * Cleared on: ManageSeatingRedirectClickAction (flow entry),
 * GetBookingDetailDataAction (booking-details visit, which then re-probes
 * when the booking holds a Nol pass), and the shared
 * navigateToBookingDetails helper in PaymentGatewayCheckoutAction
 * (called from both the MPGS callback and the 204 success branch of
 * RedirectToPaymentGatewayAction post-settlement).
 */
export interface IPendingRefund {
  bookingId: string;
  fulfillmentIds: string[];
}

export const pendingRefundFulfillmentIdsAtom = atom<IPendingRefund | null>({
  key: "pendingRefundFulfillmentIdsAtom",
  default: null,
});

/**
 * Per-ancillary refund quotes obtained by POSTing /bookings/{id}/refund-offers
 * speculatively at manage-seating entry. Lets the AddonsModal show the actual
 * refundFee + refundableAmount per deselected meal up-front, instead of
 * approximating with `removedBaselineTotal − bookingWideModFee`.
 *
 * Booking-scoped via `bookingId`; guarded by callers so a stale cache from an
 * abandoned manage-seating session can't bleed into an unrelated booking.
 *
 * Display-only under the new server-side refund pipeline — actual settlement
 * happens via /api/payment/session's `refundedAncillaries` field, not via
 * PATCH-CONFIRM of these offer ids. The cache exists purely to give the
 * AddonsModal accurate per-meal refund amounts at modal-open time.
 *
 * Cleared on: ManageSeatingRedirectClickAction (flow entry),
 * GetBookingDetailDataAction (booking-details visit, which then re-probes
 * when the booking holds a Nol pass), and the shared
 * navigateToBookingDetails helper in PaymentGatewayCheckoutAction
 * (called from both the MPGS callback and the 204 success branch of
 * RedirectToPaymentGatewayAction post-settlement).
 */
export interface IRefundQuoteEntry {
  refundOfferId: string;
  refundFee: number;
  refundableAmount: number;
}

export interface IRefundOffersByFulfillmentId {
  bookingId: string;
  entries: Record<string, IRefundQuoteEntry>;
}

export const refundOffersByFulfillmentIdAtom =
  atom<IRefundOffersByFulfillmentId | null>({
    key: "refundOffersByFulfillmentIdAtom",
    default: null,
  });

export const carriedOverAddonsAtom = atom<ICarriedOverAddons>({
  key: "carriedOverAddonsAtom",
  default: { outbound: [], inbound: [] },
});

/**
 * Shape of each item the frontend sends in the `refundedAncillaries` array
 * of POST /api/payment/session. Each entry is a deselected baseline extra
 * (FULFILLED ancillary the user un-ticked during manage-seating). Backend
 * processes them as refunds at session-settlement time alongside the new
 * charge.
 *
 * NEW PREBOOKED charges are NOT in this list — they're already attached
 * to the booking via prior POST /booked-offers/{offerId}/ancillaries, and
 * the backend computes the new charge from the booking's PREBOOKED items
 * directly. This list is refund-only.
 *
 * Built fresh for each session POST by `buildSessionAncillaries`; not
 * persisted.
 */
export interface IPaymentSessionAncillary {
  offerId: string;
  ancillaryOfferId: string;
  passengerRefs: string[];
  fulfillmentId: string;
  bookingId: string;
}

/**
 * Shape of each item the frontend sends in the `refundLegs` array of
 * POST /api/payment/session. Each entry names a booking and the fulfillments
 * on it to be cancelled and refunded when the session settles.
 *
 * Used by the change-date flow to retire the onward products (shuttle, Nol)
 * attached to the leg whose date is moving — they stay on the original date
 * and are worthless once the train moves.
 *
 * Built fresh for each session POST by `buildSessionRefundLegs`; not persisted.
 */
export interface IPaymentSessionRefundLeg {
  bookingId: string;
  fulfillmentIds: string[];
}

/**
 * Snapshot of carry-over ancillary fingerprints, populated only in the
 * change-date / change-time flow. Each fingerprint is
 * `${passengerRef}|${productCode}|${stopCode}` and identifies an addon the
 * user already paid for on the original offer (status FULFILLED or
 * EXCHANGE_ONGOING). Used by the Extras card to decide whether to render
 * a "NEW" tag on each addon row: items NOT in the snapshot are user picks
 * made this session and get the tag. Other flows (manage-seating, upgrade,
 * regular checkout) ignore this atom — their existing isPrebooked-based
 * rule continues to apply.
 *
 * Build-up: SyncAlreadyAddonsAction writes this atom alongside
 * `alreadyAddonsLegPassengerAtom` when `localStorage.isChangeDateFlow ===
 * "true"`. Tear-down: cleared by `clearBookingFlowAtoms` on every fresh
 * flow entry so a stale snapshot from an aborted flow can't bleed into
 * the next session.
 */
export const changeDateCarryOverSnapshotAtom = atom<string[]>({
  key: "changeDateCarryOverSnapshotAtom",
  default: [],
});